export type RetryQueueState = "pending" | "failed";

export interface RetryQueueItem {
  attemptCount: number;
  nextAttemptAt: string | null;
  lastError: string | null;
  state: RetryQueueState;
}

const RETRY_DELAYS_MS = [30_000, 120_000, 600_000, 3_600_000] as const;

function errorMessage(reason: unknown): string {
  return reason instanceof Error ? reason.message : String(reason || "Unknown sync error");
}

export function nextRetryAt(attemptCount: number, now = Date.now()): string {
  const index = Math.min(Math.max(attemptCount - 1, 0), RETRY_DELAYS_MS.length - 1);
  return new Date(now + (RETRY_DELAYS_MS[index] ?? 3_600_000)).toISOString();
}

export function isRetryableQueueError(reason: unknown): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  if (reason instanceof TypeError) return true;
  const message = errorMessage(reason).toLowerCase();
  return [
    "failed to fetch",
    "network request failed",
    "network error",
    "connection",
    "offline",
    "timeout",
    "timed out",
    "rate limit",
    "too many requests",
    "502",
    "503",
    "504",
  ].some((fragment) => message.includes(fragment));
}

export async function processQueueItems<T extends RetryQueueItem>(
  items: T[],
  operations: {
    save(item: T): Promise<void>;
    remove(item: T): Promise<void>;
    store(item: T): Promise<void>;
  },
  options: { force?: boolean; now?: number } = {},
): Promise<void> {
  const now = options.now ?? Date.now();
  for (const item of items) {
    const due = !item.nextAttemptAt || new Date(item.nextAttemptAt).getTime() <= now;
    if (!options.force && (item.state === "failed" || !due)) continue;

    try {
      await operations.save(item);
      await operations.remove(item);
    } catch (reason) {
      const retryable = isRetryableQueueError(reason);
      const attemptCount = item.attemptCount + 1;
      await operations.store({
        ...item,
        attemptCount,
        lastError: errorMessage(reason).slice(0, 240),
        state: retryable ? "pending" : "failed",
        nextAttemptAt: retryable ? nextRetryAt(attemptCount, now) : null,
      });
    }
  }
}
